import { type Source } from "../model";
import styles from "./messageSource.module.css";
import {
  NJAccordion,
  NJAccordionItem,
  NJAccordionItemHeader,
  NJAccordionItemContent,
  NJButton,
  NJSpinner,
} from "@engie-group/fluid-design-system-react";
import { downloadFile } from "./downloadFile";
import { openPdfAtPage } from "./openPdfAtPage";
import { httpClient } from "@genai/http-client";
import { useQuery } from "@tanstack/react-query";
import { fetchFile } from "@genai/file";
import { getTokensSync } from "@genai/auth";
import { StoredFile } from "@genai/collection";
import DOMPurify from "dompurify";

const SourceImage = (props: { pdfUrl: string; pageNumber: number }) => {
  const { pdfUrl, pageNumber } = props;
  const src = `${pdfUrl}?page=${pageNumber}`;
  const tokens = getTokensSync();

  const headers = {
    Authorization: tokens?.idToken || '',
  };
  const { data, isLoading, error } = useQuery({
    queryKey: ["image", src],
    queryFn: () => httpClient(src, { headers }).then((res) => res.blob()),
  });
  if (isLoading) return <NJSpinner scale="xs" aria-label="" />;
  if (error) return <div>Erreur lors du chargement de l'image</div>;
  if (!data) return null;
  return (
    <img
      src={URL.createObjectURL(data)}
      alt="Source image"
      onClick={async () => openPdfAtPage(pdfUrl, pageNumber + 1, headers)}
    />
  );
};

const PdfSource = (props: {
  downloadUrl: string;
  file: StoredFile | undefined;
  pages: number[];
}) => {
  const { downloadUrl, file, pages } = props;
  return (
    <>
      <NJAccordionItemHeader>
        {file?.name}
      </NJAccordionItemHeader>

      <NJAccordionItemContent>
        <div className={styles["genai-chat-ai-accordion-content_list"]}>
          <p>
            Les pages suivantes ont été sélectionnées et triées selon vos critères de priorité.
            <br /> Elles sont classées par ordre de pertinence selon votre demande.
          </p>
          <div className={styles["page-list"]}>
            {pages?.map((page, index) => (
              <span key={index} className={styles["page-item"]} >Page : {page + 1}</span>
            ))}
          </div>

          <div className={styles["page-list-container"]}>
            {pages?.map((pageNumber, index) => (
              <div key={index} className={styles["page-item_img"]} >
                <SourceImage
                  pdfUrl={downloadUrl}
                  pageNumber={pageNumber}
                />
                <span>Page: {pageNumber + 1}</span>
              </div>
            ))}
          </div>
        </div>
        <div className={styles["genai-chat-ai-accordion-content-footer"]}>
          <NJButton
            emphasis="minimal"
            icon="download"
            label="Télécharger"
            onClick={() => downloadFile(downloadUrl, file?.name)}
          />
        </div>
      </NJAccordionItemContent>
    </>
  );
};


const PptSource = (props: {
  downloadUrl: string;
  file: StoredFile | undefined;
  pages: number[];
}) => {
  const { downloadUrl, file, pages } = props;
  return (
    <>
      <NJAccordionItemHeader>
        {file?.name}
      </NJAccordionItemHeader>
      <NJAccordionItemContent>
        <div className={styles["genai-chat-ai-accordion-content-text"]}>
          <p>
            Les pages suivantes ont été sélectionnées et triées selon vos critères de priorité.
            <br /> Elles sont classées par ordre de pertinence selon votre demande.
          </p>
          <span className={styles["page-list"]}>
            {pages?.map((page, index) => (
              <span key={index} className={styles["page-item"]} >Page : {page + 1}</span>
            ))}
          </span>
        </div>
        <div className={styles["genai-chat-ai-accordion-content-footer"]}>
          <NJButton
            emphasis="minimal"
            icon="download"
            label="Télécharger"
            onClick={() => downloadFile(downloadUrl, file?.name)}
          />

        </div>
      </NJAccordionItemContent>
    </>
  );
};

const WordSource = (props: {
  htmlContent: string;
  file: StoredFile | undefined;
}) => {
  const { file, htmlContent } = props;
  const sanitizedHtml = DOMPurify.sanitize(htmlContent);

  return (
    <>
      <NJAccordionItemHeader>{file?.name} - </NJAccordionItemHeader>
      <NJAccordionItemContent>
        <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }}></div>
      </NJAccordionItemContent>
    </>
  );
};

export const MessageSource = (props: { source: Source }) => {
  const { fileId, collectionId, mimetype } = props.source;

  const downloadUrl = `/collections/${collectionId}/files/${fileId}/download`;
  const { data, isLoading, error } = useQuery({
    queryKey: ["file", collectionId, fileId],
    queryFn: () => fetchFile(collectionId, fileId),
  });

  if (isLoading)
    return (
      <NJAccordion className={styles["genai-chat-ai-accordion"]}>
        <NJAccordionItem>
          <NJAccordionItemHeader>
            <NJSpinner scale="xs" aria-label="" />
          </NJAccordionItemHeader>
        </NJAccordionItem>
      </NJAccordion>
    );
  if (error)
    return (
      <NJAccordion className={styles["genai-chat-ai-accordion"]}>
        <NJAccordionItem>
          <NJAccordionItemHeader>Échec du chargement de la source</NJAccordionItemHeader>
        </NJAccordionItem>
      </NJAccordion>
    );

  return (
    <NJAccordion className={styles["genai-chat-ai-accordion"]}>
      <NJAccordionItem>
        {
          {
            ["application/vnd.openxmlformats-officedocument.presentationml.presentation"]: (
              <PptSource
                file={data}
                pages={(props.source as { pages: number[] }).pages}
                downloadUrl={downloadUrl}
              />
            ),
            ["application/pdf"]: (
              <PdfSource
                file={data}
                pages={(props.source as { pages: number[] }).pages}
                downloadUrl={downloadUrl}
              />
            ),
            ["application/msword"]: (
              <WordSource
                file={data}
                htmlContent={
                  (props.source as { htmlContent: string }).htmlContent
                }
              />
            ),
            ["application/vnd.openxmlformats-officedocument.wordprocessingml.document"]:
              (
                <WordSource
                  file={data}
                  htmlContent={
                    (props.source as { htmlContent: string }).htmlContent
                  }
                />
              ),
          }[mimetype]
        }
      </NJAccordionItem>
    </NJAccordion>
  );
};
